import React, { useEffect, useState } from "react";
import { formattedNumber, generateSilkImagePath, getSilkImagePath } from "@/app/utils/Common";
import styled from "styled-components";
import Table from '@mui/material/Table';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import { LANGTYPE_EN, useLanguage } from "@/app/utils/LanguageContext";
import { textSize, text_size } from "@/app/utils/MixinSize";
import { useMyContext } from "@/app/utils/MyContext";

const Container = styled('div')<{ $assetPrefix: string }>(({ $assetPrefix }) => `
  padding: 12px 24px; 
  justify-content: space-around;
  width: 100%; 
  border-radius: 4px 4px 0 0;
  margin-top: 12px; 
  background-image: url(${$assetPrefix}/HorseInfo_Banner.png); 
  background-size: cover; 
  background-position: left center; 
  background-repeat: no-repeat; 
`);
const ContainerM = styled.div`
  justify-content: space-around;
  width: 100%; 
  padding: 12px 0 0 0; 
`;
const DivHorseHeader = styled.div` 
  display: flex;
  width: 100%;
  padding: 0 0 12px 0;
`;
const DivHorseHeaderImage = styled.div` 
  display: flex;
  width: 68px;
  min-width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #FFFFFF;
  align-items: center;
  justify-content: center;
  img {
    width: 50px;
    height: 50px;
    border-radius: 4px;
  }
`;

const DivHorseHeaderImageM = styled.div` 
  display: flex;
  width: 54px;
  min-width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #FFFFFF;
  align-items: center;
  justify-content: center;
  img {
    width: 40px;
    height: 40px;
    border-radius: 4px;
  }
`;
const DivHorseHeaderContent = styled.div` 
  padding: 0 0 0 10px; 
  color: #FFFFFF; 
`;

const DivHorseContent = styled.div` 
  display: flex;
  padding: 12px 0; 
  justify-content: space-around;
  width: 100%;
  border-radius: 4px;
  background: #FFFFFF;
`; 
const StyledItemHeaderContent = styled.span` 
  display: inline-block;
  ${text_size(24)}
  font-weight: 700;
`;
const StyledItemHeaderContentM = styled.span` 
  display: inline-block;
  ${text_size(18)}
  font-weight: 700;
  line-height: 24px;
  padding: 8px 0;
`;
const StyledItemHeaderContentSub = styled.span` 
  display: inline-block;
  ${text_size(13)}
  font-weight: 300;
  margin-right: 14px;
`;
const StyledItemHeaderContentSubM = styled.div` 
  display: inline-block;
  ${text_size(13)}
  font-weight: 300;
  width: 150px;
`;

const DivHorseContentCol = styled.div` 
  width: 33.33%;
  border-right: 1px solid #B4B4B4;
  padding: 0 12px;
  div {
    display: flex;
  }

  &:last-child {
    border-right: none;
  }
`;
const DivHorseContentColM = styled.div` 
  width: 100%;
  padding: 0 12px;
  div {
    display: flex;
  }
`;
const StyledItemFirst = styled.div` 
  display: flex;
  min-width: 80px;
  width: 80px;
  color: #122C68;
  ${text_size(13)}
  font-weight: 300;
  vertical-align: top;
`;
const StyledItemSecond = styled.div` 
  display: flex;
  ${text_size(13)}
  font-weight: 500;
  color: #000000;
  vertical-align: top;
`;

const StyledTableRow = styled(TableRow)(() => ({
  'td, th': {
    border: 0,
    ...textSize(13),
    color: "#000000",
    fontWeight: 500,
    verticalAlign: "top",
    padding: "0 12px",
    lineHeight: "20px",
  },
  'td:first-child, th:first-child': {
    minWidth: "80px",
    width: "25%",
    padding: "0 0 0 12px",
    color: "#122C68",
    fontWeight: 300,
  },
}));


interface Props {
  type: string;
  data: any;
  meetingDate: string;
  meetingVenueCode: string;
  raceNo: number;
  horseNo: number;
  horseStatus: string;
  totalRun: number;
}

export default function BasicInfo(props: Props) { 
  const { state: myContextState } = useMyContext();
  const { state: lang } = useLanguage();
  const { t } = lang;
  const horseData = props.data;
  const colorPath = getSilkImagePath(props.meetingDate, props.meetingVenueCode, props.raceNo, props.horseNo)

  const perStats = horseData?.performanceStats?.filter((x: any) => x.type == "LifeTime")?.[0];
  const totalRun = props.totalRun;
  return props.type === "pc" ? (
      <Container $assetPrefix={myContextState.assetPrefix}>
      <DivHorseHeader>
        <DivHorseHeaderImage> 
            <img src={generateSilkImagePath(colorPath, lang.langCode)} alt={colorPath} 
            key={colorPath} onError={(e:any) => {
              e.target.style.display = 'none';
              e.target.parentElement.style.background="none";
            }} /> 
        </DivHorseHeaderImage>
        <DivHorseHeaderContent>
          <div>
            <StyledItemHeaderContent id="horse_profile_pc_name">{lang.langType === LANGTYPE_EN ? horseData?.name_en : horseData?.name_ch}</StyledItemHeaderContent></div>
          <div style={{display:"flex"}}>
            <div style={{width:"260px",display:"flex"}}>
              <StyledItemHeaderContentSub id="horse_profile_pc_country_age_title">{t("horse_profile_country_age")}</StyledItemHeaderContentSub>
              <StyledItemHeaderContentSub id="horse_profile_pc_country_age_content" style={{ fontWeight: 500}}>{lang.langType === LANGTYPE_EN ? horseData?.countryOfOrigin?.code : horseData?.countryOfOrigin?.chinese} / {horseData?.age}</StyledItemHeaderContentSub>
            </div>
            <div style={{width:"260px",display:"flex"}}>
              <StyledItemHeaderContentSub id="horse_profile_pc__colour_sex_title">{t("horse_profile_colour_sex")}</StyledItemHeaderContentSub>
              <StyledItemHeaderContentSub id="horse_profile_pc__colour_sex_content" style={{ fontWeight: 500}}>{lang.langType === LANGTYPE_EN ? horseData?.colour?.english || "-": horseData?.colour?.chinese || "-"} / {lang.langType === LANGTYPE_EN ? horseData?.gender?.english || "-" : horseData?.gender?.chinese || "-"}</StyledItemHeaderContentSub>
            </div>
          </div>
        </DivHorseHeaderContent>
      </DivHorseHeader>
      <DivHorseContent id="horse_profile_pc_content">
        <DivHorseContentCol id="horse_profile_pc_stakes_starts_rating">
          <div>
            <StyledItemFirst style={{width:"140px"}}>{t("horse_profile_total_stakes")}</StyledItemFirst>
            <StyledItemSecond>{totalRun >= 0 ? "HK$" + formattedNumber(horseData?.earings || 0) : "-"}</StyledItemSecond>
          </div>
          <div>
            <StyledItemFirst style={{width:"140px"}}>{t("horse_profile_no_of_starts")}</StyledItemFirst>
            {totalRun >= 0 ?
            <StyledItemSecond>{perStats?.firstPlace}-{perStats?.secondPlace}-{perStats?.thirdPlace}-{perStats?.totalRun}</StyledItemSecond>
            : <StyledItemSecond>----</StyledItemSecond>
            }
          </div>
          <div>
            <StyledItemFirst style={{width:"140px"}}>{t("horse_profile_international_rating")}</StyledItemFirst>
            <StyledItemSecond>{Number(horseData?.rating ?? -1) > 0 ? horseData?.rating : "-"}</StyledItemSecond>
          </div>
        </DivHorseContentCol>
        <DivHorseContentCol id="horse_profile_pc_trainer_owner">
          <div>
            <StyledItemFirst>{t("trainer")}</StyledItemFirst>
            <StyledItemSecond>{lang.langType === LANGTYPE_EN ? horseData?.trainer?.name_en || '-' : horseData?.trainer?.name_ch || '-'}</StyledItemSecond>
          </div>
          <div>
            <StyledItemFirst>{t("owner")}</StyledItemFirst>
            <StyledItemSecond>{totalRun >= 0 ? lang.langType === LANGTYPE_EN ? horseData?.ownerName?.english || '-' : horseData?.ownerName?.chinese || '-' : "-"}</StyledItemSecond>
          </div>
        </DivHorseContentCol>
        <DivHorseContentCol id="horse_profile_pc_sire_dam_damssire">
          <div>
            <StyledItemFirst>{t("sire")}</StyledItemFirst>
            <StyledItemSecond>{totalRun >= 0 ? horseData?.sire || '-' : "-"}</StyledItemSecond>
          </div>
          <div>
            <StyledItemFirst>{t("dam")}</StyledItemFirst>
            <StyledItemSecond>{totalRun >= 0 ? horseData?.dam || '-' : "-"}</StyledItemSecond>
          </div>
          <div>
            <StyledItemFirst>{t("dams_sire")}</StyledItemFirst>
            <StyledItemSecond>{totalRun >= 0 ? horseData?.sireOfDam || '-' : "-"}</StyledItemSecond>
          </div>
        </DivHorseContentCol>
      </DivHorseContent>
    </Container>
  ) : (
    <ContainerM>
    <DivHorseHeader style={{paddingLeft:"4px"}}>
      <DivHorseHeaderImageM >
          <img src={generateSilkImagePath(colorPath, lang.langCode)} alt={colorPath} key={colorPath} 
          onError={(e:any) => {
            e.target.style.display = 'none';
            e.target.parentElement.style.background="none";
          }} />
      </DivHorseHeaderImageM>
      <DivHorseHeaderContent>
        <div><StyledItemHeaderContentM id="horseProfile_name_mobile">{lang.langType === LANGTYPE_EN ? horseData?.name_en : horseData?.name_ch}</StyledItemHeaderContentM></div>
        <div>
          <div style={{width:"260px"}}>
            <StyledItemHeaderContentSubM id="horseProfile_country_age_title_mobile">{t("horse_profile_country_age")}</StyledItemHeaderContentSubM>
            <StyledItemHeaderContentSub id="horseProfile_country_age_content_mobile" style={{ fontWeight: 500}}>{lang.langType === LANGTYPE_EN ? horseData?.countryOfOrigin?.code : horseData?.countryOfOrigin?.chinese} / {horseData?.age}</StyledItemHeaderContentSub>
          </div>
          <div style={{width:"260px"}}>
            <StyledItemHeaderContentSubM id="horseProfile_colour_sex_title_mobile">{t("horse_profile_colour_sex")}</StyledItemHeaderContentSubM>
            <StyledItemHeaderContentSub id="horseProfile_colour_sex_content_mobile" style={{ fontWeight: 500}}>{lang.langType === LANGTYPE_EN ? horseData?.colour?.english || "-" : horseData?.colour?.chinese || "-"} / {lang.langType === LANGTYPE_EN ? horseData?.gender?.english || "-" : horseData?.gender?.chinese || "-"}</StyledItemHeaderContentSub>
          </div>
        </div>
      </DivHorseHeaderContent>
    </DivHorseHeader>
    <DivHorseContent id="horseProfile_content_mobile">
      <Table aria-label="simple table">
        <TableBody>
          <StyledTableRow>
            <TableCell>{t("horse_profile_total_stakes")}</TableCell>
            <TableCell>{totalRun >= 0 ? "HK$" + formattedNumber(horseData?.earings || 0) : "-"}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <TableCell>{t("horse_profile_no_of_starts")}</TableCell>
            {totalRun >= 0 ? 
            <TableCell>{perStats?.firstPlace}-{perStats?.secondPlace}-{perStats?.thirdPlace}-{perStats?.totalRun}</TableCell> 
            : <TableCell>----</TableCell>}
            
          </StyledTableRow>
          <StyledTableRow>
              <TableCell>{t("horse_profile_international_rating")}</TableCell>
              <TableCell>{Number(horseData?.rating ?? -1) > 0 ? horseData?.rating : "-"}</TableCell>
            </StyledTableRow>
          <StyledTableRow>
            <TableCell>{t("trainer")}</TableCell>
            <TableCell>{lang.langType === LANGTYPE_EN ? horseData?.trainer?.name_en || '-' : horseData?.trainer?.name_ch || '-'}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <TableCell>{t("owner")}</TableCell>
            <TableCell>{totalRun >= 0 ? lang.langType === LANGTYPE_EN ? horseData?.ownerName?.english || '-' : horseData?.ownerName?.chinese || '-' : "-"}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <TableCell>{t("sire")}</TableCell>
            <TableCell>{totalRun >= 0 ? horseData?.sire || '-' : "-"}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <TableCell>{t("dam")}</TableCell>
            <TableCell>{totalRun >= 0 ? horseData?.dam || '-' : "-"}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <TableCell>{t("dams_sire")}</TableCell>
            <TableCell>{totalRun >= 0 ? horseData?.sireOfDam || '-' : "-"}</TableCell>
          </StyledTableRow>
        </TableBody>
      </Table>
    </DivHorseContent>
  </ContainerM>
  );
} 
